// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Box, Button, Center, Input, Loader, Modal, Stack } from '@mantine/core';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { AddressElement, CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { botIdentifier } from '../../lib/bot-identifier';
import { getStripePromise } from '../../lib/stripe';
import { friendlyErrorMessage } from './settings.utils';

interface AddCardModalProps {
  opened: boolean;
  patientId: string;
  onClose: () => void;
  onSaved: (patient: Patient) => void;
}

const STRIPE_BOT = 'stripe-payment-method';

export function AddCardModal({ opened, patientId, onClose, onSaved }: AddCardModalProps): JSX.Element {
  const medplum = useMedplum();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [intentError, setIntentError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retryToken is a bumped counter used solely to re-trigger this effect when the user clicks Retry
  useEffect(() => {
    if (!opened) {
      setClientSecret(null);
      setIntentError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = (await medplum.executeBot(
          botIdentifier(STRIPE_BOT),
          { op: 'create-setup-intent', patientId },
          'application/json'
        )) as { clientSecret: string; customerId: string };
        if (!cancelled) {
          setClientSecret(result.clientSecret);
          setIntentError(null);
        }
      } catch (err) {
        Sentry.captureException(err);
        if (!cancelled) {
          setIntentError(friendlyErrorMessage(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, medplum, patientId, retryToken]);

  return (
    <Modal opened={opened} onClose={onClose} title="Add a card" size="lg" centered>
      {intentError && (
        <Alert color="red" title="We couldn't start the card form">
          <Stack gap="sm">
            <span>{intentError}</span>
            <Button variant="default" onClick={() => setRetryToken((t) => t + 1)}>
              Retry
            </Button>
          </Stack>
        </Alert>
      )}

      {!intentError && !clientSecret && (
        <Center mih={200}>
          <Loader />
        </Center>
      )}

      {!intentError && clientSecret && (
        <Elements stripe={getStripePromise()} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
          <CardForm patientId={patientId} clientSecret={clientSecret} onSaved={onSaved} onClose={onClose} />
        </Elements>
      )}
    </Modal>
  );
}

interface CardFormProps {
  patientId: string;
  clientSecret: string;
  onClose: () => void;
  onSaved: (patient: Patient) => void;
}

function CardForm({ patientId, clientSecret, onClose, onSaved }: CardFormProps): JSX.Element {
  const medplum = useMedplum();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [persistError, setPersistError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    setSubmitting(true);
    setStripeError(null);
    setPersistError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeError('Card element not ready. Please try again.');
      setSubmitting(false);
      return;
    }

    const addressElement = elements.getElement(AddressElement);
    const addressValue = addressElement ? await addressElement.getValue() : null;
    const billingDetails = addressValue?.complete
      ? {
          address: {
            line1: addressValue.value.address.line1,
            line2: addressValue.value.address.line2,
            city: addressValue.value.address.city,
            state: addressValue.value.address.state,
            postal_code: addressValue.value.address.postal_code,
            country: addressValue.value.address.country,
          },
          name: addressValue.value.name,
        }
      : undefined;

    const result = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: cardElement, billing_details: billingDetails },
    });

    if ('error' in result && result.error) {
      setStripeError(result.error.message ?? 'We could not save your card. Please check the details and try again.');
      setSubmitting(false);
      return;
    }

    const setupIntent = (result as { setupIntent?: { payment_method?: string } }).setupIntent;
    const paymentMethodId = typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : undefined;
    if (!paymentMethodId) {
      setStripeError('Stripe did not return a payment method id. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await Sentry.startSpan({ op: 'http.client', name: 'confirm-payment-method' }, async () => {
        await medplum.executeBot(
          botIdentifier(STRIPE_BOT),
          { op: 'confirm-payment-method', patientId, paymentMethodId },
          'application/json'
        );
        const refreshed = await medplum.readResource('Patient', patientId);
        onSaved(refreshed);
      });
      onClose();
    } catch (err) {
      Sentry.captureException(err);
      setPersistError("Your card was saved, but we couldn't refresh the page. Please reload to see it.");
      setSubmitting(false);
    }
  }, [stripe, elements, clientSecret, medplum, patientId, onSaved, onClose]);

  return (
    <Stack gap="md">
      <AddressElement options={{ mode: 'billing' }} />
      <Input.Wrapper label="Card details">
        <Box
          style={{
            border: '1px solid var(--mantine-color-default-border)',
            borderRadius: 'var(--mantine-radius-sm)',
            padding: '8px 12px',
            backgroundColor: 'var(--mantine-color-default)',
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#212529',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  '::placeholder': { color: '#adb5bd' },
                },
                invalid: { color: '#fa5252' },
              },
            }}
          />
        </Box>
      </Input.Wrapper>
      {stripeError && <Alert color="red">{stripeError}</Alert>}
      {persistError && <Alert color="red">{persistError}</Alert>}
      <Button onClick={handleSubmit} loading={submitting} disabled={!stripe || !elements}>
        Save card
      </Button>
    </Stack>
  );
}
